import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  TextField,
} from "@mui/material";
import { BarChart, LineChart, PieChart } from "@mui/x-charts";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Warning,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import axios from "axios";
import { tokens } from "../dashboard/theme";
import NavigationHeader from "../components/NavigationHeader";
import config from "./global/config";
import fr from "date-fns/locale/fr"; // French localization

const StatCard = ({ title, value, icon: Icon, trend, loading, color }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: colors.primary[900],
        height: "100%",
        minHeight: 160,
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6],
        },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -20,
          top: -20,
          width: 120,
          height: 120,
          borderRadius: "50%",
          backgroundColor: color,
          opacity: 0.1,
          zIndex: 0,
        }}
      />

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography
                variant="h6"
                color={colors.grey[100]}
                mb={1}
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "0.875rem",
                  letterSpacing: "0.1em",
                }}
              >
                {title}
              </Typography>
              <Typography
                variant="h3"
                color={color}
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                }}
              >
                {typeof value === "number"
                  ? value.toLocaleString("fr-FR", {
                      style: "currency",
                      currency: "MAD",
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : value}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: color,
                borderRadius: "50%",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.9,
              }}
            >
              <Icon sx={{ fontSize: 24, color: colors.primary[900] }} />
            </Box>
          </Box>

          {trend !== undefined && (
            <Box display="flex" alignItems="center" gap={1}>
              {trend > 0 ? (
                <ArrowUpward
                  sx={{ color: colors.greenAccent[500], fontSize: 20 }}
                />
              ) : (
                <ArrowDownward
                  sx={{ color: colors.redAccent[500], fontSize: 20 }}
                />
              )}
              <Typography
                variant="body2"
                color={
                  trend > 0 ? colors.greenAccent[500] : colors.redAccent[500]
                }
                sx={{ fontWeight: "bold" }}
              >
                {Math.abs(trend)}%
              </Typography>
              <Typography variant="body2" color={colors.grey[100]}>
                depuis le mois dernier
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

function Dashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date());
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const api = axios.create({
    baseURL: config.apiUrl,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const calculateTrend = (current, previous) => {
    if (!previous) return 0;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  const fetchDashboardData = async (year) => {
    try {
      const response = await api.get("dashboard.php", {
        params: {
          year: year.getFullYear(),
        },
      });
      setData(response.data);

      if (
        response.data.monthly_trends &&
        response.data.monthly_trends.length > 0
      ) {
        setTrends(response.data.monthly_trends);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
      if (error.response?.status === 401) {
        window.location.href = "/login";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(selectedYear);
  }, [selectedYear]);

  const calculateStatTrends = () => {
    if (!trends || trends.length < 2) return {};

    const currentMonth = trends[trends.length - 1];
    const previousMonth = trends[trends.length - 2];

    return {
      profits: calculateTrend(currentMonth.profits, previousMonth.profits),
      losses: calculateTrend(currentMonth.losses, previousMonth.losses),
    };
  };

  const statTrends = calculateStatTrends();

  const getMinYear = () => {
    const date = new Date();
    return new Date(date.getFullYear() - 10, 0, 1);
  };

  const getMaxYear = () => {
    return new Date();
  };

  return (
    <Box m="25px">
      <Box
        display="flex"
        flexDirection={isNonMobile ? "row" : "column"}
        justifyContent="space-between"
        alignItems={isNonMobile ? "center" : "flex-start"}
        gap={2}
        mb={3}
      >
        <NavigationHeader
          title="Tableau de Bord"
          subtitle="Bienvenue dans votre aperçu financier"
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
          <DatePicker
            views={["year"]}
            label="Sélectionner l'année"
            value={selectedYear}
            onChange={(newValue) => {
              setSelectedYear(newValue);
            }}
            minDate={getMinYear()}
            maxDate={getMaxYear()}
            sx={{
              backgroundColor: colors.primary[900],
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.grey[100],
                },
                "&:hover fieldset": {
                  borderColor: colors.grey[200],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.grey[300],
                },
              },
              "& .MuiInputLabel-root": {
                color: colors.grey[100],
              },
              "& .MuiInputBase-input": {
                color: colors.grey[100],
              },
              "& .MuiSvgIcon-root": {
                color: colors.grey[100],
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      <Grid container spacing={3}>
        {/* Stat Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Profits Totaux"
            value={data?.profits}
            icon={TrendingUp}
            trend={statTrends.profits}
            loading={loading}
            color={colors.greenAccent[500]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pertes Totales"
            value={data?.losses}
            icon={TrendingDown}
            trend={statTrends.losses}
            loading={loading}
            color={colors.redAccent[500]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Dettes Clients"
            value={data?.customer_debts}
            icon={AccountBalance}
            loading={loading}
            color={colors.blueAccent[500]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Montants en Retard"
            value={data?.overdue_amounts}
            icon={Warning}
            loading={loading}
            color={colors.orangeAccent[500]}
          />
        </Grid>

        {/* Charts */}
        {!loading && trends.length > 0 && (
          <>
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[900],
                  height: "400px",
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Typography variant="h6" color={colors.grey[100]} mb={2}>
                  Aperçu Mensuel Profits & Pertes
                </Typography>
                <BarChart
                  series={[
                    {
                      data: trends.map((item) => item.profits),
                      label: "Profits",
                      color: colors.greenAccent[500],
                    },
                    {
                      data: trends.map((item) => item.losses),
                      label: "Pertes",
                      color: colors.redAccent[500],
                    },
                  ]}
                  xAxis={[
                    {
                      data: trends.map((item) => item.month),
                      scaleType: "band",
                    },
                  ]}
                  height={300}
                  slotProps={{
                    legend: {
                      labelStyle: {
                        fill: colors.grey[100],
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[900],
                  height: "400px",
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Typography variant="h6" color={colors.grey[100]} mb={2}>
                  Répartition Financière
                </Typography>
                <PieChart
                  series={[
                    {
                      data: [
                        {
                          id: 0,
                          value: data.profits,
                          label: "Profits",
                          color: colors.greenAccent[500],
                        },
                        {
                          id: 1,
                          value: data.losses,
                          label: "Pertes",
                          color: colors.redAccent[500],
                        },
                        {
                          id: 2,
                          value: data.customer_debts,
                          label: "Dettes",
                          color: colors.blueAccent[500],
                        },
                      ],
                    },
                  ]}
                  height={300}
                  slotProps={{
                    legend: {
                      labelStyle: {
                        fill: colors.grey[100],
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  backgroundColor: colors.primary[900],
                  height: "400px",
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              >
                <Typography variant="h6" color={colors.grey[100]} mb={2}>
                  Tendances Financières
                </Typography>
                <LineChart
                  series={[
                    {
                      data: trends.map((item) => item.profits),
                      label: "Profits",
                      color: colors.greenAccent[500],
                    },
                    {
                      data: trends.map((item) => item.losses),
                      label: "Pertes",
                      color: colors.redAccent[500],
                    },
                  ]}
                  xAxis={[
                    {
                      data: trends.map((item) => item.month),
                      scaleType: "point",
                    },
                  ]}
                  height={300}
                  slotProps={{
                    legend: {
                      labelStyle: {
                        fill: colors.grey[100],
                      },
                    },
                  }}
                />
              </Paper>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
}

export default Dashboard;
